import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { invalidateBrowseAllSurveyTemplatesQuery } from '../useBrowseAllSurveyTemplatesQuery';
import { invalidateBrowseSurveyTemplatesQuery } from '../useBrowseSurveyTemplatesQuery';
import { invalidateSearchSurveyTemplatesQuery } from '../useSearchSurveyTemplatesQuery';
import { invalidateSurveyTemplateQuery } from '../useSurveyTemplateQuery';
import { invalidateSurveyTemplatesByIndustryQuery } from '../useSurveyTemplatesByIndustryQuery';
import { invalidateSystemSurveyTemplateQuery } from '../useSystemSurveyTemplateQuery';

export interface IUpdateSurveyTemplateParams {
	id: string;
	name?: string;
	summary?: string;
	keywords?: string[];
	surveyDefinition?: Api.ICustomSurvey;
	enabled?: boolean;
}

export interface IUpdateSurveyTemplateOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.ISurveyTemplate) => void;
}

/**
 * Update an existing survey template via the admin API.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const useUpdateSurveyTemplateMutation = (options: IUpdateSurveyTemplateOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: IUpdateSurveyTemplateParams) => {
			const { id, ...updateData } = params;

			return userSession.webServiceHelper.callAsync<Api.ISurveyTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/surveytemplate/${id}`,
				}),
				'PUT',
				updateData
			);
		},
		onSuccess: data => {
			// Invalidate via the queries' own invalidators — the hand-rolled keys used here
			// previously matched no query, so edits didn't refetch until a manual reload.
			invalidateSystemSurveyTemplateQuery(data.id);
			invalidateSurveyTemplateQuery(data.id);
			invalidateSurveyTemplatesByIndustryQuery();
			invalidateBrowseAllSurveyTemplatesQuery();
			invalidateBrowseSurveyTemplatesQuery();
			invalidateSearchSurveyTemplatesQuery();
			onSuccess?.(data);
		},
		onError,
	});
};
